import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import type {
  AgentMessage,
  AgentSession,
  AgentModelsResponse,
  AgentDrawerTab,
  AgentToolCall,
  AgentImageResult,
} from '../../interface/agent.interface';
import { agentApi } from '../../services/agentApi';

// ── Async thunks ────────────────────────────────────────────────────

export const fetchAgentModels = createAsyncThunk(
  'agent/fetchModels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentApi.getModels();
      return response.data;
    } catch (error: any) {
      console.error('[fetchAgentModels] Error:', error);
      return rejectWithValue(
        error?.response?.data?.error ||
          i18n.t('toast.failed_to_fetch_models', 'Failed to fetch models'),
      );
    }
  },
);

// ── State shape ─────────────────────────────────────────────────────

interface AgentState {
  /** Whether the right-side drawer is open */
  drawerOpen: boolean;
  /** Active tab inside the drawer */
  activeTab: AgentDrawerTab;

  /** Current session messages */
  messages: AgentMessage[];
  /** Whether the AI is currently streaming a response */
  isStreaming: boolean;

  /** All past sessions */
  sessions: AgentSession[];
  /** Current session ID */
  currentSessionId: string | null;

  /** Available models from backend */
  models: AgentModelsResponse | null;
  modelsLoading: boolean;
  modelsError: string | null;
  /** Currently selected model ID */
  selectedModel: string;
  /** Selected image backend */
  imageBackend: string;

  /** Content type selected in content tab */
  selectedContentType: string | null;
  /** Scenario selected */
  selectedScenario: string | null;
  /** Agent memory text */
  agentMemory: string;
}

const initialState: AgentState = {
  drawerOpen: false,
  activeTab: 'chat',

  messages: [],
  isStreaming: false,

  sessions: [],
  currentSessionId: null,

  models: null,
  modelsLoading: false,
  modelsError: null,
  selectedModel: '',
  imageBackend: 'imagen3',

  selectedContentType: null,
  selectedScenario: null,
  agentMemory: '',
};

// ── Helper: generate unique ID ──────────────────────────────────────
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ── Slice ───────────────────────────────────────────────────────────

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    /** Toggle drawer open/close */
    toggleDrawer(state) {
      state.drawerOpen = !state.drawerOpen;
    },
    setDrawerOpen(state, action: PayloadAction<boolean>) {
      state.drawerOpen = action.payload;
    },

    /** Switch active tab */
    setActiveTab(state, action: PayloadAction<AgentDrawerTab>) {
      state.activeTab = action.payload;
    },

    /** Add a user message */
    addUserMessage(state, action: PayloadAction<string>) {
      state.messages.push({
        id: uid(),
        role: 'user',
        content: action.payload,
        timestamp: Date.now(),
      });
    },

    /** Start a new assistant message (streaming) */
    startAssistantMessage(state) {
      state.isStreaming = true;
      state.messages.push({
        id: uid(),
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        toolCalls: [],
        isStreaming: true,
      });
    },

    /** Append a text token to the last assistant message */
    appendToken(state, action: PayloadAction<string>) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.content += action.payload;
      }
    },

    /** Set final text for the last assistant message */
    setFinalText(state, action: PayloadAction<string>) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.content = action.payload;
      }
    },

    /** Add a tool call to the last assistant message */
    addToolCall(state, action: PayloadAction<AgentToolCall>) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        if (!last.toolCalls) last.toolCalls = [];
        last.toolCalls.push(action.payload);
      }
    },

    /** Attach image result to the last assistant message */
    setImageResult(state, action: PayloadAction<AgentImageResult>) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.imageResult = action.payload;
      }
    },

    /** Mark streaming complete */
    finishStreaming(state) {
      state.isStreaming = false;
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
      }
    },

    /** Model selection */
    setSelectedModel(state, action: PayloadAction<string>) {
      state.selectedModel = action.payload;
    },

    /** Image backend selection */
    setImageBackend(state, action: PayloadAction<string>) {
      state.imageBackend = action.payload;
    },

    /** Content type selection */
    setSelectedContentType(state, action: PayloadAction<string | null>) {
      state.selectedContentType = action.payload;
    },

    /** Scenario selection */
    setSelectedScenario(state, action: PayloadAction<string | null>) {
      state.selectedScenario = action.payload;
    },

    /** Agent memory */
    setAgentMemory(state, action: PayloadAction<string>) {
      state.agentMemory = action.payload;
    },

    /** Save current chat as a session */
    saveCurrentSession(state) {
      if (state.messages.length === 0) return;
      const sessionId = state.currentSessionId || uid();
      const firstUserMsg = state.messages.find((m) => m.role === 'user');
      const title = firstUserMsg
        ? firstUserMsg.content.slice(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '')
        : i18n.t('toast.new_session', 'New Session');

      const existingIdx = state.sessions.findIndex((s) => s.id === sessionId);
      const session: AgentSession = {
        id: sessionId,
        title,
        messages: [...state.messages],
        createdAt: existingIdx >= 0 ? state.sessions[existingIdx].createdAt : Date.now(),
        updatedAt: Date.now(),
      };

      if (existingIdx >= 0) {
        state.sessions[existingIdx] = session;
      } else {
        state.sessions.unshift(session);
      }
      state.currentSessionId = sessionId;
    },

    /** Load a session */
    loadSession(state, action: PayloadAction<string>) {
      const session = state.sessions.find((s) => s.id === action.payload);
      if (session) {
        state.messages = [...session.messages];
        state.currentSessionId = session.id;
      }
    },

    /** Start a new chat */
    newChat(state) {
      state.messages = [];
      state.currentSessionId = null;
      state.isStreaming = false;
      state.selectedContentType = null;
      state.selectedScenario = null;
    },

    /** Delete a session */
    deleteSession(state, action: PayloadAction<string>) {
      state.sessions = state.sessions.filter((s) => s.id !== action.payload);
      if (state.currentSessionId === action.payload) {
        state.messages = [];
        state.currentSessionId = null;
      }
    },

    /** Reset entire agent state */
    resetState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentModels.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(fetchAgentModels.fulfilled, (state, action) => {
        state.modelsLoading = false;
        state.models = action.payload;
        // Set default selected model if not already set
        if (!state.selectedModel && action.payload.active_model) {
          state.selectedModel = action.payload.active_model;
        }
      })
      .addCase(fetchAgentModels.rejected, (state, action) => {
        console.error('[Redux] fetchAgentModels.rejected:', action.payload);
        state.modelsLoading = false;
        state.modelsError =
          (action.payload as string) ||
          i18n.t('toast.failed_to_fetch_models', 'Failed to fetch models');
      });
  },
});

export const {
  toggleDrawer,
  setDrawerOpen,
  setActiveTab,
  addUserMessage,
  startAssistantMessage,
  appendToken,
  setFinalText,
  addToolCall,
  setImageResult,
  finishStreaming,
  setSelectedModel,
  setImageBackend,
  setSelectedContentType,
  setSelectedScenario,
  setAgentMemory,
  saveCurrentSession,
  loadSession,
  newChat,
  deleteSession,
  resetState,
} = agentSlice.actions;

export default agentSlice.reducer;
